import { HECTOR_HEADING_STYLE_LEVELS, type CanvasActionInput, type CanvasSelector } from '@hector/shared-types';
import type { CanvasSnapshotBlock } from './canvasSnapshot';

type CanvasPlacement = Extract<CanvasActionInput, { action: 'insert_paragraphs' }>['placement'];

/**
 * Le style effectif d'un bloc, borné par CE QUE LA LECTURE PAR BLOC PUBLIE.
 *
 * ## La règle, inchangée : ne jamais copier ce qu'on ne sait pas relire
 *
 * Une facette copiée sans pouvoir être relue produit un receipt qui prétend
 * l'avoir appliquée sans pouvoir le vérifier. La liste ci-dessous n'est donc pas
 * une liste de souhaits : c'est le relevé de ce que le fournisseur publie.
 *
 * ## Le relevé — `superdoc@2.6.0`, mesuré le 2026-08-16 (#991)
 *
 * `tests/ws-mocked/mesure-lecture-par-bloc.spec.ts` pose onze facettes par leur
 * méthode nommée, confirme chaque pose à l'écran (`getComputedStyle`), puis
 * cherche la facette dans les deux surfaces de lecture par bloc.
 *
 * - **`blocks.list` est devenue MUETTE sur le style.** Union des clés sur les
 *   douze entrées : `ordinal / nodeId / nodeType / textPreview / text /
 *   isEmpty`, plus `styleId` et `headingLevel` sur le seul bloc portant un style
 *   nommé. `bold`, `underline`, `color`, `fontFamily`, `fontSize`, `alignment`
 *   et `paragraphNumbering` sont DÉCLARÉS par `BlockListEntry` et ne sont
 *   JAMAIS remplis — alors que les mêmes facettes sont peintes à l'écran.
 *   C'est ce qui a fait refuser le pinceau six fois d'affilée : il ne pouvait
 *   plus rien lire, donc plus rien copier.
 * - **`getNodeById` publie, lui, les propriétés de paragraphe** :
 *   `props.alignment`, `props.indent` (POINTS), `props.spacing` (points, `line`
 *   en multiple quand `lineRule:'auto'`), et `styleRef` / `heading.level`.
 *
 * ## Ce qui reste volontairement hors du pinceau, et pourquoi
 *
 * - **Marques de run** — italique, gras, souligné, police, taille, couleur,
 *   surlignage : AUCUNE des deux surfaces ne les publie en 2.6.0 (le nœud SDM
 *   rend un `run:{text}` nu), alors qu'elles sont peintes. Illisibles, donc non
 *   copiées. Les champs `bold` / `underline` / `color` / `fontFamily` /
 *   `fontSizePt` restent au contrat : ils sont encore alimentés le jour où
 *   `blocks.list` recommence à les publier, et par le `styleId` explicite.
 * - **Bordure et ombrage de paragraphe** : posés sans erreur, publiés par
 *   aucune lecture, et — pour la bordure — jamais peints par le canvas (#1007).
 * - **`numbering.marker`** : le libellé rendu (« 11. ») diffère légitimement de
 *   celui de la source (« 10. ») quand le bloc inséré rejoint la séquence.
 */
export interface EffectiveBlockStyle {
  paragraphStyleId?: string;
  fontFamily?: string;
  /** Font size in points — the unit both blocks.list and format.apply use. */
  fontSizePt?: number;
  color?: string;
  bold?: boolean;
  underline?: boolean;
  alignment?: 'left' | 'center' | 'right' | 'justify';
  /** Direct indentation in twips — the unit both blocks.list `indent` and format.paragraph.setIndentation use. */
  indentation?: { leftTwips?: number; rightTwips?: number; firstLineTwips?: number; hangingTwips?: number };
  /** Numbering reference (`w:numPr`): joining the same numId continues the sequence. */
  numbering?: { numId?: number; level?: number };
  /**
   * Espacement de paragraphe, dans l'unité que le nœud SDM publie : POINTS pour
   * `before` / `after`, et pour `line` un MULTIPLE d'interligne quand
   * `lineRule` vaut `'auto'` (mesuré : 240 twips posés → 12 pt relus ;
   * `line:360` posé → `1.5` relu). Chaque surface d'écriture reconvertit selon
   * son propre contrat — voir `styleActionExecutors`.
   */
  spacing?: { beforePt?: number; afterPt?: number; line?: number; lineRule?: string };
}

function isRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
}

function stringValue(input: unknown): string | undefined {
  return typeof input === 'string' && input.trim().length > 0 ? input : undefined;
}

function numberValue(input: unknown): number | undefined {
  return typeof input === 'number' && Number.isFinite(input) ? input : undefined;
}

function booleanValue(input: unknown): boolean | undefined {
  return typeof input === 'boolean' ? input : undefined;
}

function alignmentValue(input: unknown): EffectiveBlockStyle['alignment'] {
  return input === 'left' || input === 'center' || input === 'right' || input === 'justify' ? input : undefined;
}

function compactRecord<T extends Record<string, unknown>>(input: T): Partial<T> | undefined {
  const entries = Object.entries(input).filter(([, value]) => value !== undefined);
  return entries.length === 0 ? undefined : Object.fromEntries(entries) as Partial<T>;
}

/**
 * Le style effectif OBSERVABLE d'un bloc — `{}` quand la projection provider
 * n'en rapporte aucune facette.
 *
 * ⚠️ **Ne jamais laisser cette fonction rendre `undefined`** (#980, mesuré le
 * 2026-08-15). Elle rendait `compactRecord(...) as EffectiveBlockStyle`, or
 * `compactRecord` rend `undefined` quand tous ses champs le sont — le `as`
 * masquait le cas au typage. Latent en 1.44.1, où `blocks.list()` rapportait
 * toujours au moins une facette ; fatal sous `superdoc@2`, dont la projection
 * s'est appauvrie (#986) : sur un document blanc elle n'en rapporte aucune, et
 * les deux lectures `captureEffectiveBlockStyle(block).paragraphStyleId` de
 * `styleActionExecutors` levaient un `TypeError` À TRAVERS toute l'action
 * agent — présenté à l'agent comme une « SuperDoc error », c'est-à-dire en
 * accusant le provider d'un défaut qui était le nôtre.
 *
 * `{}` est la valeur juste, pas un pis-aller : « aucune facette observable »
 * est précisément ce que `hasObservableEffectiveStyle` doit rendre faux, et ce
 * qui doit faire échouer une comparaison de style — donc déclencher la
 * réparation prévue, au lieu d'interrompre l'action.
 */
export function captureEffectiveBlockStyle(block: Record<string, unknown>): EffectiveBlockStyle {
  const indent = isRecord(block.indent) ? block.indent : undefined;
  const indentation = indent === undefined
    ? undefined
    : compactRecord({
        leftTwips: numberValue(indent.left),
        rightTwips: numberValue(indent.right),
        firstLineTwips: numberValue(indent.firstLine),
        hangingTwips: numberValue(indent.hanging),
      });
  const paragraphNumbering = isRecord(block.paragraphNumbering) ? block.paragraphNumbering : undefined;
  const numbering = paragraphNumbering === undefined
    ? undefined
    : compactRecord({
        numId: numberValue(paragraphNumbering.numId),
        level: numberValue(paragraphNumbering.level),
      });

  return compactRecord({
    paragraphStyleId: stringValue(block.styleId),
    fontFamily: stringValue(block.fontFamily),
    fontSizePt: numberValue(block.fontSize),
    color: stringValue(block.color),
    bold: booleanValue(block.bold),
    underline: booleanValue(block.underline),
    alignment: alignmentValue(block.alignment),
    indentation: indentation as EffectiveBlockStyle['indentation'],
    numbering: numbering as EffectiveBlockStyle['numbering'],
  }) ?? {};
}

/** 1 pt = 20 twips (OOXML). Le nœud SDM publie des points, `setIndentation` veut des twips. */
const TWIPS_PAR_POINT = 20;

function pointsVersTwips(points: number | undefined): number | undefined {
  return points === undefined ? undefined : Math.round(points * TWIPS_PAR_POINT);
}

/**
 * La charge utile d'un nœud SDM, quelle que soit sa nature.
 *
 * Un `kind:'paragraph'` la porte sous `node.paragraph`, un `kind:'heading'`
 * sous `node.heading` (relevé du 2026-08-16). Chercher `paragraph` seul
 * perdrait tout le style des TITRES, silencieusement — c'est-à-dire exactement
 * sur les blocs les plus stylés du document.
 */
function chargeUtileDuNoeud(node: Record<string, unknown>): Record<string, unknown> | undefined {
  const kind = typeof node.kind === 'string' ? node.kind : undefined;
  const direct = kind === undefined ? undefined : node[kind];
  if (isRecord(direct)) return direct;
  return isRecord(node.paragraph) ? node.paragraph : isRecord(node.heading) ? node.heading : undefined;
}

/**
 * Le style effectif tel que la lecture PAR BLOC du modèle documentaire le
 * publie (`doc.getNodeById` → `SDNodeResult`), `{}` quand elle n'en publie
 * aucune facette.
 *
 * ⚠️ Comme `captureEffectiveBlockStyle`, cette fonction ne rend JAMAIS
 * `undefined` : « aucune facette observable » est une réponse, et c'est celle
 * qui doit faire conclure « ce bloc est au style par défaut du document » —
 * pas celle qui doit interrompre l'action.
 *
 * Elle accepte aussi bien l'enveloppe `{node, address}` que le nœud nu, parce
 * que la forme rendue est un contrat du fournisseur et non une certitude : se
 * tromper d'un niveau rendrait un `{}` parfaitement cohérent et entièrement
 * faux.
 */
export function captureEffectiveStyleFromSdmNode(input: unknown): EffectiveBlockStyle {
  if (!isRecord(input)) return {};
  const node = isRecord(input.node) ? input.node : input;
  const payload = chargeUtileDuNoeud(node);
  if (payload === undefined) return {};
  const props = isRecord(payload.props) ? payload.props : undefined;
  const indent = isRecord(props?.indent) ? props.indent : undefined;
  const spacingBrut = isRecord(props?.spacing) ? props.spacing : undefined;

  const indentation = indent === undefined
    ? undefined
    : compactRecord({
        // `start` / `end` sont les alias bidirectionnels de `left` / `right` :
        // les ignorer perdrait le retrait d'un document rédigé dans ce dialecte.
        leftTwips: pointsVersTwips(numberValue(indent.left) ?? numberValue(indent.start)),
        rightTwips: pointsVersTwips(numberValue(indent.right) ?? numberValue(indent.end)),
        firstLineTwips: pointsVersTwips(numberValue(indent.firstLine)),
        hangingTwips: pointsVersTwips(numberValue(indent.hanging)),
      });
  const spacing = spacingBrut === undefined
    ? undefined
    : compactRecord({
        beforePt: numberValue(spacingBrut.before),
        afterPt: numberValue(spacingBrut.after),
        line: numberValue(spacingBrut.line),
        lineRule: stringValue(spacingBrut.lineRule),
      });
  const numbering = isRecord(props?.numbering)
    ? compactRecord({
        // SDParagraphProps.numbering.numId est une CHAÎNE, w:numId un entier.
        numId: numberValue(Number(props.numbering.numId)),
        level: numberValue(props.numbering.level),
      })
    : undefined;

  return compactRecord({
    paragraphStyleId: stringValue(payload.styleRef),
    alignment: alignmentValue(props?.alignment),
    indentation: indentation as EffectiveBlockStyle['indentation'],
    spacing: spacing as EffectiveBlockStyle['spacing'],
    numbering: numbering as EffectiveBlockStyle['numbering'],
  }) ?? {};
}

/**
 * Réunit deux lectures du MÊME bloc — une facette publiée par l'une des
 * surfaces suffit à être copiée.
 *
 * L'ordre compte : `prioritaire` gagne, facette par facette. Une facette
 * absente du prioritaire n'efface JAMAIS celle du repli — un signal absent dit
 * « je ne sais pas », pas « ça vaut zéro ».
 */
export function fusionnerStylesEffectifs(
  repli: EffectiveBlockStyle,
  prioritaire: EffectiveBlockStyle,
): EffectiveBlockStyle {
  // Le filtrage est load-bearing : un spread laisse passer une clé PRÉSENTE et
  // valant `undefined`, qui écraserait la facette du repli par « rien ».
  const retenues = Object.entries(prioritaire).filter(([, valeur]) => valeur !== undefined);
  return { ...repli, ...Object.fromEntries(retenues) } as EffectiveBlockStyle;
}

function stableValue(input: unknown): unknown {
  if (Array.isArray(input)) return input.map(stableValue);
  if (!isRecord(input)) return input;
  return Object.fromEntries(
    Object.keys(input)
      .sort()
      .filter((key) => input[key] !== undefined)
      .map((key) => [key, stableValue(input[key])]),
  );
}

export function effectiveStyleFingerprint(style: EffectiveBlockStyle | undefined): string {
  return JSON.stringify(stableValue(style ?? {}));
}

export function hasObservableEffectiveStyle(style: EffectiveBlockStyle | undefined): boolean {
  return style !== undefined && Object.keys(stableValue(style) as Record<string, unknown>).length > 0;
}

/**
 * Les facettes de POLICE qu'une insertion ou un pinceau ne sait pas prouver
 * quand la lecture ne les a pas publiées sur la source.
 *
 * ## Pourquoi ces deux-là, et pourquoi un reçu doit les nommer
 *
 * En `superdoc@2.15.1`, seule la mise en forme DIRECTE d'un run est lisible :
 * une police ou une taille HÉRITÉE d'un style (le cas de presque tout corps de
 * texte) n'est publiée par aucune lecture, et `includeResolved` n'est jamais
 * rempli. La comparaison d'empreintes ne porte donc que sur ce qui a été lu —
 * elle peut être verte sans rien dire de la police. Reçus d'insertion du
 * 2026-09-30 : `formattingMatched:true` alors que police et taille n'avaient été
 * observées nulle part ; l'agent a annoncé à l'avocat un acte « conforme ».
 *
 * Le nom rendu est celui du vocabulaire agent (`fontSize`, pas `fontSizePt`).
 */
export const FACETTES_DE_POLICE = [
  { facette: 'fontFamily', nom: 'fontFamily' },
  { facette: 'fontSizePt', nom: 'fontSize' },
] as const satisfies ReadonlyArray<{ facette: keyof EffectiveBlockStyle; nom: string }>;

export type FacetteDePoliceNonObservee = (typeof FACETTES_DE_POLICE)[number]['nom'];

/**
 * Les facettes de police que la source ne publiait pas — donc qu'aucune
 * comparaison n'a pu vérifier, quel que soit le verdict de l'empreinte.
 *
 * `undefined` en source = « la lecture ne l'a pas dit », jamais « police par
 * défaut » : un signal absent n'est pas un signal négatif.
 */
export function facettesDePoliceNonObservees(source: EffectiveBlockStyle | undefined): FacetteDePoliceNonObservee[] {
  return FACETTES_DE_POLICE
    .filter(({ facette }) => source?.[facette] === undefined)
    .map(({ nom }) => nom);
}

/**
 * Ce que `formattingMatched` a le DROIT d'affirmer.
 *
 * - écart observé → `false` ;
 * - concordance sur tout ce qui a été lu, mais une facette de police jamais
 *   observée → `undefined` (champ ABSENT) : on ne sait pas, donc on ne dit rien,
 *   et la vérification `formatting_not_observable` nomme ce qui manque ;
 * - concordance sur tout, police comprise → `true`.
 */
export function formattingMatchedObservable(
  concordanceObservee: boolean,
  nonObservees: readonly FacetteDePoliceNonObservee[],
): boolean | undefined {
  if (!concordanceObservee) return false;
  return nonObservees.length === 0 ? true : undefined;
}

/**
 * Le style d'un paragraphe VIDE : ses seules facettes de paragraphe.
 *
 * Un paragraphe vide (espacement entre deux paragraphes d'un acte) n'a aucun
 * run : une police ou un gras n'ont rien sur quoi se poser, `format.apply` n'a
 * aucune plage à viser, et la relecture ne peut pas les rendre. Les exiger
 * ferait échouer la vérification d'une insertion parfaitement conforme.
 */
export function facettesDeParagrapheSeules(style: EffectiveBlockStyle): EffectiveBlockStyle {
  return compactRecord({
    paragraphStyleId: style.paragraphStyleId,
    alignment: style.alignment,
    indentation: style.indentation,
    numbering: style.numbering,
    spacing: style.spacing,
  }) ?? {};
}

/**
 * Choisit le bloc dont le style sera COPIÉ quand l'agent n'a demandé aucune
 * `styleSource` — c'est-à-dire le comportement par défaut de `insert_paragraphs`.
 *
 * ## La règle : le dernier paragraphe de CORPS qui précède le point d'insertion
 *
 * À défaut, le premier paragraphe de corps qui le suit ; à défaut encore,
 * `undefined`, que l'appelant traite en insertion au style par défaut du
 * document (`document_default`) plutôt qu'en refus.
 *
 * ## Pourquoi l'ancre n'est JAMAIS la source par nature
 *
 * - #1024 (2026-08-22) : insérer « après le titre DISCUSSION » donnait aux
 *   paragraphes le style `Hector-H2` du titre — l'ancre était à la fois la
 *   position et le patron.
 * - Conclusions prud'homales du 2026-09-30 : le filtre ne regardait que
 *   `nodeType` et la présence de texte. Un titre écrit en paragraphe « Normal »
 *   gras + souligné, ou un « PAR CES MOTIFS » centré, passait pour du corps :
 *   les paragraphes insérés après sortaient gras + soulignés, ou centrés.
 *   `create.paragraph` crée pourtant en « Normal » (sondé) : l'héritage venait
 *   de CE choix de source.
 *
 * Un paragraphe de corps est donc : un `paragraph` non vide, sans niveau de
 * titre, dont le style lu n'est ni un style de titre, ni centré, ni gras ET
 * souligné à la fois (le titre « à la main » des actes d'avocat). La lecture
 * de style est asynchrone (`getNodeById` publie l'alignement, pas `blocks.list`)
 * et n'est faite que sur les candidats, du plus proche au plus lointain.
 *
 * Ce n'est PAS une interdiction : une `styleSource` explicite ne passe jamais
 * par ici — `styleSourceForInput` l'honore, titre compris.
 */
export async function selectDefaultInsertionStyleSource(
  blocks: readonly CanvasSnapshotBlock[],
  placement: CanvasPlacement,
  lireStyle: (block: CanvasSnapshotBlock) => Promise<EffectiveBlockStyle | undefined>,
  ancreResolueNodeId?: string,
): Promise<CanvasSnapshotBlock | undefined> {
  const pointDInsertion = indexDuPointDInsertion(blocks, placement, ancreResolueNodeId);
  if (pointDInsertion === undefined) return undefined;
  const estCorps = async (bloc: CanvasSnapshotBlock): Promise<boolean> =>
    estCandidatDeCorps(bloc) && estStyleDeCorps(await lireStyle(bloc));
  for (let index = pointDInsertion - 1; index >= 0; index -= 1) {
    const bloc = blocks[index];
    if (bloc !== undefined && await estCorps(bloc)) return bloc;
  }
  for (let index = pointDInsertion; index < blocks.length; index += 1) {
    const bloc = blocks[index];
    if (bloc !== undefined && await estCorps(bloc)) return bloc;
  }
  return undefined;
}

/**
 * Index du premier bloc qui SUIT le point d'insertion : les blocs `[0, i)` le
 * précèdent. `undefined` quand l'ancre est introuvable — on ne se rabat pas sur
 * un voisin arbitraire.
 */
function indexDuPointDInsertion(
  blocks: readonly CanvasSnapshotBlock[],
  placement: CanvasPlacement,
  ancreResolueNodeId: string | undefined,
): number | undefined {
  if (placement.at === 'document_start') return 0;
  if (placement.at === 'document_end') return blocks.length;
  // L'exécuteur a DÉJÀ résolu l'ancre avec le vrai résolveur (normalisation,
  // occurrence, match) : se fier à son nodeId. Re-chercher le texte ici pouvait
  // désigner un autre bloc (apostrophes typographiques, occurrence) et faire
  // copier le style du mauvais voisin (revue 2026-10-01).
  const ancre = ancreResolueNodeId !== undefined
    ? blocks.findIndex((block) => block.nodeId === ancreResolueNodeId)
    : indexDeLAncre(blocks, placement.selector);
  if (ancre < 0) return undefined;
  return placement.at === 'after' ? ancre + 1 : ancre;
}

function indexDeLAncre(blocks: readonly CanvasSnapshotBlock[], selector: CanvasSelector): number {
  if (selector.kind === 'nodeId') return blocks.findIndex((block) => block.nodeId === selector.nodeId);
  if (selector.kind === 'ordinal') return blocks.findIndex((block) => block.ordinal === selector.value);
  const terms = selector.terms.map((term) => term.toLocaleLowerCase('fr-FR'));
  return blocks.findIndex((block) => terms.every((term) => block.fullText.toLocaleLowerCase('fr-FR').includes(term)));
}

/** Filtre sans lecture : ce qui se sait depuis la liste des blocs. */
function estCandidatDeCorps(block: CanvasSnapshotBlock): boolean {
  return block.nodeType === 'paragraph'
    && block.fullText.trim().length > 0
    && block.headingLevel === undefined
    && !estStyleDeTitre(block.effectiveStyle?.styleId)
    && block.effectiveStyle?.alignment !== 'center';
}

/** Filtre sur le style LU du bloc (`blocks.list` + `getNodeById`). */
function estStyleDeCorps(style: EffectiveBlockStyle | undefined): boolean {
  if (style === undefined) return true;
  return !estStyleDeTitre(style.paragraphStyleId)
    && style.alignment !== 'center'
    && !(style.bold === true && style.underline === true);
}

/**
 * Styles nommés qui font d'un paragraphe un TITRE : niveaux Hector, intitulés
 * de section Hector centrés (`PLAISE`, `PAR CES MOTIFS`), et styles Word
 * intégrés (« Heading 1 », « Titre1 », « Title »…), quel que soit l'export.
 */
const STYLES_HECTOR_DE_SECTION = new Set(['Hector-PLAISE', 'Hector-PAR-CES-MOTIFS']);
const STYLE_WORD_DE_TITRE = /^(heading|titre|title|subtitle|sous-titre)\s*\d*$/i;

function estStyleDeTitre(styleId: string | undefined): boolean {
  if (styleId === undefined) return false;
  return HECTOR_HEADING_STYLE_LEVELS[styleId] !== undefined
    || STYLES_HECTOR_DE_SECTION.has(styleId)
    || STYLE_WORD_DE_TITRE.test(styleId.trim());
}
